/**
 * Who is using this browser: GET /api/session → teacher, student, CSRF token and instance
 * facts (operator, demo/Google/AI availability). One request per page load; sign-in hooks
 * replace the cached value directly, and any 401 (or a sign-out in another tab) makes us ask
 * again.
 */
import type { InstanceInfoDTO, SessionDTO, StudentDTO, TeacherDTO } from '@galley/shared'
import { useQueryClient } from '@tanstack/react-query'
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react'
import { onUnauthorized, setCsrfToken, type ApiError } from '../api/client'
import { SESSION_CHANNEL, useSessionQuery } from '../api/hooks'
import { isHubSite } from '../app/site'
import { keys } from '../api/keys'

export type SessionStatus = 'loading' | 'ready' | 'error'

export interface SessionContextValue {
  status: SessionStatus
  session: SessionDTO | null
  teacher: TeacherDTO | null
  student: StudentDTO | null
  csrf: string | null
  instance: InstanceInfoDTO | null
  error: ApiError | null
  refetch: () => void
}

const SessionContext = createContext<SessionContextValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  // The Curator's Hub's site has no Galley server: no teacher, student or household to ask
  // about, and no "can't reach Galley" page for the /api/session it doesn't have.
  const hubSite = isHubSite()
  const query = useSessionQuery({ enabled: !hubSite })
  const { data, error, isPending, refetch } = query

  useEffect(() => {
    if (data) setCsrfToken(data.csrf)
  }, [data])

  useEffect(
    () =>
      onUnauthorized(() => {
        void qc.invalidateQueries({ queryKey: keys.session() })
      }),
    [qc],
  )

  // Signed out in another tab: ask again, so guards redirect and live streams close here too.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return
    const channel = new BroadcastChannel(SESSION_CHANNEL)
    channel.onmessage = () => {
      void qc.invalidateQueries({ queryKey: keys.session() })
    }
    return () => channel.close()
  }, [qc])

  const value = useMemo<SessionContextValue>(() => {
    // Keep showing a known session through a failed background refetch.
    const status: SessionStatus = hubSite || data ? 'ready' : isPending ? 'loading' : 'error'
    return {
      status,
      session: data ?? null,
      teacher: data?.teacher ?? null,
      student: data?.student ?? null,
      csrf: data?.csrf ?? null,
      instance: data?.instance ?? null,
      error: data || hubSite ? null : error,
      refetch: () => void refetch(),
    }
  }, [data, error, isPending, refetch, hubSite])

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>')
  return ctx
}
